"use client";
import React, { useState, useEffect, startTransition, useCallback, useRef, useMemo } from "react";
import { Button } from "../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../ui/dropdown-menu";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { 
  Plus, 
  FileText, 
  Link, 
  Upload, 
  Search, 
  ExternalLink, 
  X,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Loader2
} from "lucide-react";
import { RichTextEditor } from "./RichTextEditor";
import { LinkDocumentDialog } from "./LinkDocumentDialog";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface DocumentItem {
  id: string;
  type: 'textbox' | 'linked' | 'uploaded';
  name: string;
  reference?: string;
  url?: string;
  content?: string; // For textbox
}

interface DocumentManagerProps {
  sectionKey: string;
  groupKey?: string;
  itemId: string;
  itemQuestion?: string;
  itemTextResponse?: any; // TextResponse from SharePoint/loaded item
  itemDocuments?: DocumentItem[]; // Documents from loaded item
  onDocumentsChange?: (documents: DocumentItem[]) => void;
  onTextResponseChange?: (textResponse: any) => void;
  disabled?: boolean;
}

export function DocumentManager({ 
  sectionKey, 
  groupKey, 
  itemId, 
  itemQuestion,
  itemTextResponse,
  itemDocuments,
  onDocumentsChange,
  onTextResponseChange,
  disabled = false
}: DocumentManagerProps) {
  const { isArchivalMode } = useArchivalMode();
  const effectiveDisabled = disabled || isArchivalMode;
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showTextbox, setShowTextbox] = useState(false);
  const [textboxContent, setTextboxContent] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [hasTextbox, setHasTextbox] = useState(false);
  const [linkedFiles, setLinkedFiles] = useState<DocumentItem[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false); // Control dropdown state to prevent conflicts with dialogs
  const loadingRef = useRef(false); // Prevent multiple simultaneous loads
  const prevTextResponseRef = useRef<any>(null); // Track previous textResponse to prevent unnecessary reloads
  const prevDocumentsRef = useRef<DocumentItem[] | undefined>(undefined); // Track previous documents to prevent unnecessary reloads
  const isMountedRef = useRef(false); // Track if component is mounted
  const textboxEditorRef = useRef<HTMLDivElement>(null); // Ref for contentEditable div

  // Helper function to get workspace name from areaName/prefix
  const getWorkspaceNameFromSectionKey = async (sectionKey: string): Promise<string> => {
    // Check if this is a Concluding Procedures section
    if (sectionKey.startsWith('concluding-')) {
      const areaName = sectionKey.replace('concluding-', '').toUpperCase();
      const workspaceNameMap: Record<string, string> = {
        'CARO': 'CARO',
        'AUDREP': 'Audit Report & Opinion',
        'CTCWG': 'Communication with those charged with governance',
        'SUBSEV': 'Subsequent Events',
        'SE': 'Subsequent Events',
        'WR': 'Obtain Written Representations',
        'LIT': 'Litigations & Claims',
        'CLR': 'Compliance with laws and regulations',
        'REMB': 'Review of estimates for management bias',
        'ITAUD': 'Internal Audit',
        'GC': 'Going Concern',
        'EARM': 'Evaluate Audit Results Including Misstatements'
      };
      
      // Try to get from Sections.json first
      if (window.sharePointAPI?.downloadSectionsJson) {
        try {
          const result = await window.sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data?.ConcludingProcedures) {
            // Find workspace name by prefix
            for (const [workspaceName, prefix] of Object.entries(result.data.ConcludingProcedures)) {
              if (prefix === areaName) {
                return workspaceName;
              }
            }
          }
        } catch (error) {
          console.warn('Failed to get workspace name from Sections.json, using fallback', error);
        }
      }
      
      // Fallback to mapping
      return workspaceNameMap[areaName] || areaName;
    }
    return '';
  };

  // Helper function to get section prefix based on sectionKey
  const getSectionPrefix = (sectionKey: string): string => {
    const prefixMap: Record<string, string> = {
      'engagement-acceptance': 'EAC',
      'understanding-entity': 'UEE',
      'materiality': 'MAT',
      'preliminary-analytical': 'PAR',
      'fraud-risk': 'FRA',
      'it-risk': 'ITRA',
      'payroll': 'Payroll', // Special handling for payroll
      'ppe-additions-composition': 'PPE_ADL', // Special handling for PPE additions composition
      'ppe-deletions-composition': 'PPE_ADL' // Special handling for PPE deletions composition
    };
    return prefixMap[sectionKey] || 'PLAN';
  };
  
  // Helper function to get section name for file operations
  const getSectionName = async (sectionKey: string, itemId: string): Promise<string> => {
    // Misstatement sections - format: Misstatement_<MIS-ID>_<question_id>
    // The sectionKey is already in the correct format, so return it as-is
    if (sectionKey.startsWith('Misstatement_')) {
      return sectionKey;
    }

    // Deficiency template sections
    // Format: Deficiency_<DEF-ID>_<area>
    if (sectionKey.startsWith('deficiency-')) {
      const deficiencyId = sectionKey.replace('deficiency-', '');
      return `Deficiency_${deficiencyId}_${itemId}`;
    }

    // Check if this is a Concluding Procedures section
    if (sectionKey.startsWith('concluding-')) {
      const areaName = sectionKey.replace('concluding-', '').toUpperCase();
      // For tailoring questions, use prefix instead of workspace name
      // Format: ConcludingProcedures_<workspace_prefix>_<question_id>
      // Check if this is a tailoring question context (itemId starts with the prefix)
      const isTailoringQuestion = itemId.startsWith(areaName + '-') || 
                                   itemId.startsWith('LIT-') || 
                                   itemId.startsWith('CARO-') ||
                                   itemId.startsWith('AUDREP-') ||
                                   itemId.startsWith('CTCWG-') ||
                                   itemId.startsWith('SE-') ||
                                   itemId.startsWith('CLR-') ||
                                   itemId.startsWith('REMB-') ||
                                   itemId.startsWith('ITAUD-') ||
                                   itemId.startsWith('GC-') ||
                                   itemId.startsWith('EARM-');
      
      if (isTailoringQuestion) {
        // Use the prefix directly for tailoring questions
        return `ConcludingProcedures_${areaName}_${itemId}`;
      }
      
      // For EARM, use prefix instead of workspace name
      if (areaName === 'EARM' || sectionKey === 'concluding-evaluate-audit-results') {
        return `ConcludingProcedures_EARM_${itemId}`;
      }
      const workspaceName = await getWorkspaceNameFromSectionKey(sectionKey);
      // Format: ConcludingProcedures_<Workspace name>_<question id>
      return `ConcludingProcedures_${workspaceName}_${itemId}`;
    }
    
    if (sectionKey === 'payroll') {
      return `Execution_EBE_${itemId}`;
    }
    // Special handling for PPE composition analysis - format: Execution_PPE_ADL-<chart name>
    if (sectionKey === 'ppe-additions-composition' || sectionKey === 'ppe-deletions-composition') {
      return `Execution_PPE_ADL-${itemId}`;
    }
    // Special handling for Investments Substantive - format: Execution_INVST_<question_id>
    if (sectionKey === 'invst-substantive') {
      return `Execution_INVST_${itemId}`;
    }
    // Special handling for Intangible Assets Substantive - format: Execution_IT_<question_id>
    if (sectionKey === 'it-substantive') {
      return `Execution_IT_${itemId}`;
    }
    // Special handling for Other Expenses Substantive - format: Execution_OE_<section_name>
    if (sectionKey === 'otex-substantive') {
      return `Execution_OE_${itemId}`;
    }
    // Special handling for Provisions & Contingent Liabilities Substantive - format: Execution_PCL_<question_id>
    if (sectionKey === 'pcl-substantive') {
      return `Execution_PCL_${itemId}`;
    }
    // Special handling for Execution tailoring questions - format: Execution_<prefix>_<question_id>
    // sectionKey format: "execution-tailoring:<areaName>" or just "execution-tailoring"
    if (sectionKey === 'execution-tailoring' || sectionKey.startsWith('execution-tailoring:')) {
      // Extract areaName from sectionKey if present
      const areaName = sectionKey.includes(':') ? sectionKey.split(':')[1] : null;
      
      if (areaName && window.sharePointAPI?.downloadSectionsJson) {
        try {
          const result = await window.sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data?.Execution && result.data.Execution[areaName]) {
            const prefix = result.data.Execution[areaName];
            return `Execution_${prefix}_${itemId}`;
          }
        } catch (error) {
          console.warn('Failed to get workspace prefix from Sections.json for execution-tailoring, using fallback', error);
        }
      }
      
      // Fallback: try to get prefix from workspace-section-mapping
      if (areaName) {
        try {
          const { getSectionCode } = await import('../../utils/workspace-section-mapping');
          const prefix = getSectionCode(areaName);
          if (prefix) {
            return `Execution_${prefix}_${itemId}`;
          }
        } catch (error) {
          console.warn('Failed to get prefix from workspace-section-mapping', error);
        }
      }
      
      // Ultimate fallback
      return `Execution_TAILORING_${itemId}`;
    }
    // Special handling for substantive procedures - format: <Phase>_<prefix>_<question_id>
    // sectionKey format: "<phase>-substantive:<areaName>" (e.g., "execution-substantive:Employee Benefits Expense")
    if (sectionKey.includes('-substantive:')) {
      const [phasePart, areaName] = sectionKey.split(':');
      const phase = phasePart.replace('-substantive', '');
      
      // Capitalize first letter of phase (execution -> Execution, planning -> Planning, etc.)
      const phaseCapitalized = phase.charAt(0).toUpperCase() + phase.slice(1);
      // Handle ConcludingProcedures specially
      const phaseFormatted = phase === 'concludingprocedures' ? 'ConcludingProcedures' : phaseCapitalized;
      
      if (areaName && window.sharePointAPI?.downloadSectionsJson) {
        try {
          const result = await window.sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            // Check the appropriate phase section
            const phaseData = result.data[phaseFormatted];
            if (phaseData && phaseData[areaName]) {
              const prefix = phaseData[areaName];
              return `${phaseFormatted}_${prefix}_${itemId}`;
            }
          }
        } catch (error) {
          console.warn(`Failed to get workspace prefix from Sections.json for ${phase}-substantive, using fallback`, error);
        }
      }
      
      // Fallback: try to get prefix from workspace-section-mapping
      if (areaName) {
        try {
          const { getSectionCode } = await import('../../utils/workspace-section-mapping');
          const prefix = getSectionCode(areaName);
          if (prefix) {
            return `${phaseFormatted}_${prefix}_${itemId}`;
          }
        } catch (error) {
          console.warn('Failed to get prefix from workspace-section-mapping', error);
        }
      }
      
      // Ultimate fallback
      return `${phaseFormatted}_SUBSTANTIVE_${itemId}`;
    }
    const sectionPrefix = getSectionPrefix(sectionKey);
    return `Planning_${sectionPrefix}_${itemId}`;
  };

  // Load linked files from db.json based on question ID (async, non-blocking)
  const loadLinkedFiles = useCallback(async () => {
    // Prevent multiple simultaneous loads
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    
    try {
      const sectionToFind = await getSectionName(sectionKey, itemId);
      
      if (window.sharePointAPI?.loadDocumentListBySection) {
        try {
          const result = await window.sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (result?.success && result?.data) {
            const files = result.data.map((file: any) => ({
              id: `linked_${file.name}`,
              type: 'linked' as const,
              name: file.name || '',
              reference: file.reference || '',
              url: file.url || ''
            }));
            // Update state in next frame to prevent blocking
            requestAnimationFrame(() => {
              setLinkedFiles(files);
              loadingRef.current = false;
            });
          } else {
            loadingRef.current = false;
          }
        } catch (error) {
          console.error('Failed to load linked files:', error);
          loadingRef.current = false;
        }
      } else {
        loadingRef.current = false;
      }
    } catch (error) {
      console.error('Failed to load linked files:', error);
      loadingRef.current = false;
    }
  }, [sectionKey, itemId]);

  // Load existing documents for this question
  useEffect(() => {
    isMountedRef.current = true;
    loadDocuments();
    // Only load linked files once when itemId or sectionKey changes, not when props change
    loadLinkedFiles();
    
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, sectionKey, loadLinkedFiles]);
  
  // Update textbox editor innerHTML when textboxContent changes (to display HTML with formatting)
  useEffect(() => {
    if (textboxEditorRef.current && showTextbox) {
      // Only update if content is different to prevent cursor jumping
      const currentHtml = textboxEditorRef.current.innerHTML || '';
      if (textboxContent !== currentHtml) {
        // Save cursor position if possible (only if user is actively editing)
        const selection = window.getSelection();
        const isActiveEditor = textboxEditorRef.current.contains(selection?.anchorNode || null);
        const range = isActiveEditor && selection?.rangeCount ? selection.getRangeAt(0) : null;
        const cursorPosition = range ? range.startOffset : null;
        
        // Update innerHTML with HTML content (preserves formatting like bold, italic, etc.)
        textboxEditorRef.current.innerHTML = textboxContent || '';
        
        // Try to restore cursor position if it was saved (only if user was actively editing)
        if (isActiveEditor && range && cursorPosition !== null && textboxEditorRef.current.firstChild) {
          try {
            const newRange = document.createRange();
            newRange.setStart(textboxEditorRef.current.firstChild, Math.min(cursorPosition, textboxEditorRef.current.firstChild.textContent?.length || 0));
            newRange.collapse(true);
            selection?.removeAllRanges();
            selection?.addRange(newRange);
          } catch (e) {
            // Ignore cursor restoration errors
          }
        }
      }
    }
  }, [textboxContent, showTextbox]);

  // Separate effect for loading textResponse and documents from props (when loaded from SharePoint)
  useEffect(() => {
    // Only run if component is mounted
    if (!isMountedRef.current) return;
    
    // Check if references changed (quick check first)
    const textResponseRefChanged = itemTextResponse !== prevTextResponseRef.current;
    const documentsRefChanged = itemDocuments !== prevDocumentsRef.current;
    
    // If references haven't changed, no need to reload
    if (!textResponseRefChanged && !documentsRefChanged) {
      return;
    }
    
    // Only do deep comparison if references changed (to avoid expensive JSON.stringify on every render)
    let shouldReload = false;
    
    if (textResponseRefChanged) {
      // Both null/undefined means no change
      if (!itemTextResponse && !prevTextResponseRef.current) {
        shouldReload = false;
      } else {
        const prevStr = prevTextResponseRef.current ? JSON.stringify(prevTextResponseRef.current) : 'null';
        const currStr = itemTextResponse ? JSON.stringify(itemTextResponse) : 'null';
        shouldReload = prevStr !== currStr;
      }
    }
    
    if (!shouldReload && documentsRefChanged) {
      // Both null/undefined/empty means no change
      if ((!itemDocuments || itemDocuments.length === 0) && 
          (!prevDocumentsRef.current || prevDocumentsRef.current.length === 0)) {
        shouldReload = false;
      } else {
        const prevStr = prevDocumentsRef.current ? JSON.stringify(prevDocumentsRef.current) : 'null';
        const currStr = itemDocuments ? JSON.stringify(itemDocuments) : 'null';
        shouldReload = prevStr !== currStr;
      }
    }
    
    // Update refs regardless of whether we reload
    prevTextResponseRef.current = itemTextResponse;
    prevDocumentsRef.current = itemDocuments;

    if (shouldReload) {
      // Guard against the echo-back loop: when the user types in the textbox,
      // onTextResponseChange propagates the new content up to the parent, which updates
      // itemTextResponse and triggers this effect. If we reload now, loadDocuments() will
      // find the stale textbox content in itemDocuments (deferred via saveDocuments) and
      // overwrite the user's current edit. Skip the reload when:
      // 1. The incoming textResponse matches what's already in the textbox (user's own edit echo), OR
      // 2. The user's editor is currently focused (actively typing)
      const incomingHtml = itemTextResponse?.html || itemTextResponse?.plainText || '';
      const isEchoOfUserEdit = showTextbox && incomingHtml === textboxContent;
      const isEditorFocused = !!(textboxEditorRef.current && document.activeElement === textboxEditorRef.current);

      if (!isEchoOfUserEdit && !isEditorFocused) {
        loadDocuments();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemTextResponse, itemDocuments]);

  // Auto-show textbox if content exists (handled in loadDocuments)


  const loadDocuments = async () => {
    try {
      // Priority 1: Load from item props (from SharePoint)
      if (itemDocuments && itemDocuments.length > 0) {
        // Filter out textbox documents from the list
        const nonTextboxDocs = itemDocuments.filter((doc: DocumentItem) => doc.type !== 'textbox');
        // Use saveDocuments with skipCallback to prevent triggering parent re-renders
        saveDocuments(nonTextboxDocs, true);
        
        // Check for textbox in itemDocuments
        const textboxDoc = itemDocuments.find((doc: DocumentItem) => doc.type === 'textbox');
        if (textboxDoc && textboxDoc.content) {
          setTextboxContent(textboxDoc.content);
          setShowTextbox(true);
          setHasTextbox(true);
          // Save to localStorage for persistence
          localStorage.setItem(`textbox_${itemId}`, textboxDoc.content);
          return;
        }
      }
      
      // Priority 2: Load textResponse from item prop (from SharePoint)
      if (itemTextResponse) {
        // Always prefer HTML over plainText to preserve formatting (bold, italic, etc.)
        const htmlContent = itemTextResponse.html || itemTextResponse.plainText || '';
        if (htmlContent) {
          setTextboxContent(htmlContent);
          setShowTextbox(true);
          setHasTextbox(true);
          // Save to localStorage for persistence
          localStorage.setItem(`textbox_${itemId}`, htmlContent);
        }
      }
      
      // No localStorage loading - all documents come from SharePoint props
      
      // Priority 3: Load textbox content from localStorage (fallback only for textbox)
      if (!itemTextResponse && !itemDocuments?.find((doc: DocumentItem) => doc.type === 'textbox')) {
        const textboxContent = localStorage.getItem(`textbox_${itemId}`);
        if (textboxContent) {
          setTextboxContent(textboxContent);
          setShowTextbox(true);
          setHasTextbox(true);
        } else {
          setHasTextbox(false);
        }
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const saveDocuments = (newDocuments: DocumentItem[], skipCallback = false) => {
    // Update state immediately (React batches this)
    setDocuments(newDocuments);
    
    // No localStorage - documents are saved to SharePoint via parent callback
    
    // Only call parent callback if not skipping (to prevent loops when loading from props)
    if (!skipCallback && onDocumentsChange) {
      // Defer parent callback with VERY long delay to prevent UI freeze
      // setItemField does heavy synchronous work that can freeze the UI
      // Use requestIdleCallback if available for better performance
      const defer = typeof requestIdleCallback !== 'undefined'
        ? (cb: () => void) => requestIdleCallback(cb, { timeout: 1000 })
        : (cb: () => void) => setTimeout(cb, 1000);
      
      defer(() => {
        onDocumentsChange(newDocuments);
      });
    }
  };

  const handleAddTextbox = () => {
    const existingTextbox = documents.find(doc => doc.type === 'textbox');
    if (existingTextbox) {
      // If textbox exists, load its content
      setTextboxContent(existingTextbox.content || '');
    } else {
      // If no textbox exists, start with empty content
      setTextboxContent('');
    }
    setShowTextbox(true);
  };

  const handleLinkDocument = (selectedDocuments: any[]) => {
    if (selectedDocuments.length === 0) return;
    
    // Close dialog immediately - synchronous for instant feedback
    setShowLinkDialog(false);
    
    // Prepare documents data immediately but don't process yet
    const timestamp = Date.now();
    const newDocuments = selectedDocuments.map((doc, index) => ({
      id: `linked_${timestamp}_${index}`,
      type: 'linked' as const,
      name: doc.name || 'Unknown Document',
      reference: doc.reference || '',
      url: doc.url || ''
    }));
    
    // Update local state using startTransition to mark as non-urgent
    // This prevents blocking the UI thread
    const updated = [...documents, ...newDocuments];
    startTransition(() => {
      setDocuments(updated);
    });
    
    // CRITICAL: Do NOT call onDocumentsChange during linking to prevent freeze
    // The parent's setItemField does heavy synchronous work that freezes UI
    // We'll update the parent state much later in the background, or skip it entirely
    
    // No localStorage - documents are saved to SharePoint via parent callback
    
    // For substantive/custom procedures, we need to notify parent after linking completes
    // For other cases (like planning sections), we skip to prevent UI freeze
    const isSubstantiveOrCustom = sectionKey.includes('-substantive:') || groupKey === 'custom-procedure';
    
    // Defer SharePoint API call completely - run in background
    setTimeout(async () => {
      setIsLinking(true);
      
      // Use another setTimeout to ensure this runs well after UI updates
      setTimeout(async () => {
        try {
          const sectionPrefix = getSectionPrefix(sectionKey);
          // Create two sections: specific (with question_id) and general (without question_id)
          const specificSection = await getSectionName(sectionKey, itemId);
          // Skip general section for substantive procedures, misstatements, concluding procedures, etc.
          const generalSection = (sectionKey.startsWith('Misstatement_') || sectionKey === 'payroll' || sectionKey === 'ppe-additions-composition' || sectionKey === 'ppe-deletions-composition' || sectionKey.startsWith('concluding-') || sectionKey === 'invst-substantive' || sectionKey === 'it-substantive' || sectionKey === 'otex-substantive' || sectionKey === 'pcl-substantive' || sectionKey.includes('-substantive:'))
            ? null 
            : `Planning_${getSectionPrefix(sectionKey)}`;
          
          if (window.sharePointAPI?.updateFileSections) {
            const fileNames = selectedDocuments.map(doc => doc.name);
            
            // Add sections sequentially to avoid race conditions with db.json
            // First add specific section (with question_id)
            window.sharePointAPI.updateFileSections({
              fileNames,
              sectionToAdd: specificSection
            }).then((specificResult) => {
              if (specificResult?.success) {
                // Then add general section (without question_id) - only for planning, not payroll or PPE composition
                if (generalSection) {
                  return window.sharePointAPI.updateFileSections({
                    fileNames,
                    sectionToAdd: generalSection
                  });
                } else {
                  return { success: true }; // For payroll and PPE composition, we're done after adding specific section
                }
              } else {
                console.error('Failed to add specific section:', specificResult?.error);
                setIsLinking(false);
                return null;
              }
            }).then((generalResult) => {
              if (generalResult?.success) {
                // For substantive/custom procedures, notify parent after linking completes
                if (isSubstantiveOrCustom && onDocumentsChange) {
                  // Use the updated documents directly (no localStorage)
                  setTimeout(() => {
                    console.log(`[DocumentManager] Notifying parent of ${updated.length} documents for ${itemId}`);
                    onDocumentsChange(updated);
                  }, 300);
                }
                
                // Reload linked files much later
                setTimeout(() => {
                  loadLinkedFiles().catch(err => {
                    console.error('Failed to reload linked files:', err);
                  });
                }, 1500);
              } else if (generalResult) {
                console.error('Failed to add general section:', generalResult?.error);
              }
              setIsLinking(false);
            }).catch((error) => {
              console.error('Error updating db.json:', error);
              setIsLinking(false);
            });
          } else {
            // If no API available, still notify parent for substantive/custom procedures
            if (isSubstantiveOrCustom && onDocumentsChange) {
              setTimeout(() => {
                // Use the updated documents directly (no localStorage)
                onDocumentsChange(updated);
              }, 300);
            }
            setIsLinking(false);
          }
        } catch (error) {
          console.error('Failed to link documents:', error);
          setIsLinking(false);
        }
      }, 500);
    }, 500); // Start SharePoint update after a delay
  };

  const handleUploadFile = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "*/*";
    
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      console.log(`[DocumentManager] Starting upload for ${files.length} file(s)`);
      
      // Generate section name based on sectionKey (once for all files)
      const specificSection = await getSectionName(sectionKey, itemId);
      let sectionList: string[];
      let folderName: string;
      
      // Check if this is a substantive procedure (any phase)
      const isSubstantiveProcedure = sectionKey.includes('-substantive:');
      
      if (sectionKey.startsWith('Misstatement_') || sectionKey === 'payroll' || sectionKey.startsWith('concluding-')) {
        sectionList = [specificSection]; // For misstatements, payroll and concluding, only use specific section
        folderName = 'client';
      } else if (sectionKey === 'ppe-additions-composition' || sectionKey === 'ppe-deletions-composition') {
        sectionList = [specificSection]; // For PPE composition, only use Execution_PPE_ADL-<chart name>
        folderName = 'client';
      } else if (sectionKey === 'invst-substantive') {
        sectionList = [specificSection]; // For Investments Substantive, only use Execution_INVST_<question_id>
        folderName = 'juggernaut';
      } else if (sectionKey === 'it-substantive') {
        sectionList = [specificSection]; // For Intangible Assets Substantive, only use Execution_IT_<question_id>
        folderName = 'juggernaut';
      } else if (sectionKey === 'otex-substantive') {
        sectionList = [specificSection]; // For Other Expenses Substantive, only use Execution_OE_<section_name>
        folderName = 'juggernaut';
      } else if (sectionKey === 'pcl-substantive') {
        sectionList = [specificSection]; // For Provisions & Contingent Liabilities Substantive, only use Execution_PCL_<question_id>
        folderName = 'juggernaut';
      } else if (isSubstantiveProcedure) {
        // For substantive procedures (any phase), use only specific section and save to juggernaut folder
        sectionList = [specificSection];
        folderName = 'juggernaut';
      } else {
        sectionList = [specificSection, `Planning_${getSectionPrefix(sectionKey)}`]; // For planning, use both specific and general
        folderName = 'client';
      }
      
      // Skip general section for certain section keys (same logic as handleLinkDocument)
      const generalSection = (sectionKey.startsWith('Misstatement_') || sectionKey === 'payroll' || sectionKey === 'ppe-additions-composition' || sectionKey === 'ppe-deletions-composition' || sectionKey.startsWith('concluding-') || sectionKey === 'invst-substantive' || sectionKey === 'it-substantive' || sectionKey === 'otex-substantive' || sectionKey === 'pcl-substantive' || sectionKey.includes('-substantive:'))
        ? null 
        : `Planning_${getSectionPrefix(sectionKey)}`;
      
      // Process all files and collect successfully uploaded file names
      const uploadPromises = files.map((file, index) => {
        return new Promise<{ success: boolean; fileName: string; document?: DocumentItem }>((resolve) => {
          try {
            console.log(`[DocumentManager] Processing file ${index + 1}/${files.length}: ${file.name}`);
            // Convert file to base64 for upload
            const reader = new FileReader();
            reader.onload = async () => {
              const base64Content = reader.result as string;
              const base64Data = base64Content.split(',')[1]; // Remove data:type;base64, prefix
              
              // Get FY_YEAR from database dynamically if needed
              let fyYear: string | undefined = undefined;
              if (folderName === 'juggernaut') {
                fyYear = getFY_YEAR();
                if (!fyYear) {
                  throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
                }
              }
              
              // Upload to SharePoint folder with section(s)
              const uploadResult = await (window as any).sharePointAPI.uploadFile({
                fileName: file.name,
                fileContent: base64Data,
                folderName: folderName,
                fyYear: fyYear,
                referenceValue: `${itemId} - ${itemQuestion || ''}`,
                sectionList: sectionList
              });

              if (uploadResult.success) {
                console.log(`[DocumentManager] Upload successful for file ${index + 1}/${files.length}: ${file.name}`);
                // Use a more unique ID with index to ensure uniqueness
                const uploadedId = `uploaded_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;
                let fileUrl = uploadResult.url || uploadResult.fileUrl || uploadResult.data?.url || uploadResult.webUrl || '';
                
                // If no URL from upload result, try to get it from database like PPE execution phase
                if (!fileUrl) {
                  try {
                    const sectionResponse = await (window as any).sharePointAPI.loadDocumentListBySection(sectionKey);
                    console.log('SharePoint section response:', sectionResponse);
                    
                    if (sectionResponse?.success && sectionResponse?.data) {
                      const matchingFile = sectionResponse.data.find((f: any) => 
                        f.name === file.name && f.reference === (uploadResult.reference || `${itemId} - ${itemQuestion || ''}`)
                      );
                      fileUrl = matchingFile?.url || '';
                      console.log('Found matching file:', matchingFile);
                    }
                  } catch (error) {
                    console.error('Failed to get URL from SharePoint database:', error);
                  }
                }
                
                const newDocument: DocumentItem = {
                  id: uploadedId,
                  type: 'uploaded',
                  name: file.name,
                  reference: uploadResult.reference || `${itemId} - ${itemQuestion || ''}`,
                  url: fileUrl
                };
                
                console.log(`[DocumentManager] Created document for ${file.name}:`, newDocument);
                resolve({ success: true, fileName: file.name, document: newDocument });
              } else {
                console.error(`[DocumentManager] Upload failed for ${file.name}:`, uploadResult.error);
                resolve({ success: false, fileName: file.name });
              }
            };
            reader.onerror = () => {
              console.error(`[DocumentManager] Failed to read file: ${file.name}`);
              resolve({ success: false, fileName: file.name });
            };
            reader.readAsDataURL(file);
          } catch (error) {
            console.error(`[DocumentManager] Failed to upload file ${file.name}:`, error);
            resolve({ success: false, fileName: file.name });
          }
        });
      });
      
      // Wait for all uploads to complete
      console.log(`[DocumentManager] Waiting for ${uploadPromises.length} upload(s) to complete...`);
      const uploadResults = await Promise.all(uploadPromises);
      console.log(`[DocumentManager] All uploads completed. Results:`, uploadResults);
      
      // Collect successfully uploaded files
      const successfulUploads = uploadResults.filter(r => r.success && r.document);
      const uploadedFileNames = successfulUploads.map(r => r.fileName);
      const newDocuments = successfulUploads.map(r => r.document!);
      
      console.log(`[DocumentManager] Successfully uploaded ${successfulUploads.length}/${files.length} file(s)`);
      console.log(`[DocumentManager] File names to update in db.json:`, uploadedFileNames);
      
      // Update local state with all new documents
      if (newDocuments.length > 0) {
        const updated = [...documents, ...newDocuments];
        saveDocuments(updated);
        console.log(`[DocumentManager] Updated local state with ${newDocuments.length} new document(s)`);
        
        // For substantive/custom procedures, notify parent after upload completes
        const isSubstantiveOrCustom = sectionKey.includes('-substantive:') || groupKey === 'custom-procedure';
        if (isSubstantiveOrCustom && onDocumentsChange) {
          setTimeout(() => {
            onDocumentsChange(updated);
          }, 100);
        }
      }
      
      // Update db.json sections for all uploaded files
      if (uploadedFileNames.length > 0 && window.sharePointAPI?.updateFileSections) {
        console.log(`[DocumentManager] Updating db.json sections for ${uploadedFileNames.length} file(s) in folder: ${folderName}...`);
        // Add sections sequentially to avoid race conditions with db.json
        // First add specific section (with question_id)
        window.sharePointAPI.updateFileSections({
          fileNames: uploadedFileNames,
          sectionToAdd: specificSection,
          folderName: folderName
        }).then((specificResult) => {
          if (specificResult?.success) {
            // Then add general section (without question_id) - only for planning, not payroll or PPE composition
            if (generalSection) {
              return window.sharePointAPI.updateFileSections({
                fileNames: uploadedFileNames,
                sectionToAdd: generalSection,
                folderName: folderName
              });
            } else {
              return { success: true }; // For payroll and PPE composition, we're done after adding specific section
            }
          } else {
            console.error('Failed to add specific section:', specificResult?.error);
            return null;
          }
        }).then((generalResult) => {
          if (generalResult?.success) {
            console.log(`Successfully updated sections for ${uploadedFileNames.length} file(s) in db.json`);
          } else if (generalResult) {
            console.error('Failed to add general section:', generalResult?.error);
          }
          
          // Reload linked files after upload to sync with db.json (especially for execution sections)
          // Use a delay to ensure db.json has been updated
          setTimeout(() => {
            loadLinkedFiles().catch(err => {
              console.error('Failed to reload linked files after upload:', err);
            });
          }, 1500);
        }).catch((error) => {
          console.error('Error updating db.json sections:', error);
        });
      } else if (uploadedFileNames.length > 0) {
        // If updateFileSections is not available, still reload linked files
        setTimeout(() => {
          loadLinkedFiles().catch(err => {
            console.error('Failed to reload linked files after upload:', err);
          });
        }, 1500);
      }
    };
    
    input.click();
  };

  const handleSaveTextbox = () => {
    if (textboxContent.trim()) {
      // Save textbox content to localStorage
      localStorage.setItem(`textbox_${itemId}`, textboxContent);
      setHasTextbox(true);
      
      // Update the parent component with textResponse
      const updatedDocuments = [...documents];
      const textboxIndex = updatedDocuments.findIndex(doc => doc.type === 'textbox');
      
      if (textboxIndex >= 0) {
        updatedDocuments[textboxIndex] = {
          ...updatedDocuments[textboxIndex],
          content: textboxContent
        };
      } else {
        updatedDocuments.push({
          id: `textbox_${itemId}`,
          name: 'Text Document',
          type: 'textbox',
          content: textboxContent
        });
      }
      
      saveDocuments(updatedDocuments);
      
      // Also update the textResponse field directly
      // Always call onTextResponseChange, even if content is empty, to ensure it's saved
      if (onTextResponseChange) {
        onTextResponseChange({
          html: textboxContent || '',
          plainText: (textboxContent || '').replace(/<[^>]*>/g, '') // Strip HTML tags for plain text
        });
      }
    }
    // Keep textbox open - don't close it
  };

  // Auto-save method that can be called externally
  // This method reads directly from the contentEditable div to ensure we capture the latest content
  const autoSaveTextbox = () => {
    // First, try to read directly from the contentEditable div (most up-to-date source)
    let currentContent = textboxContent;
    if (textboxEditorRef.current && showTextbox) {
      const editorContent = textboxEditorRef.current.innerHTML;
      if (editorContent && editorContent.trim().length > 0) {
        currentContent = editorContent;
        // Update state to match editor content
        setTextboxContent(editorContent);
        console.log(`Auto-save: Read content directly from editor for ${itemId}`);
      }
    }
    
    console.log(`Auto-save called for ${itemId}:`, {
      hasContent: !!currentContent.trim(),
      showTextbox,
      contentLength: currentContent.length
    });
    
    if (currentContent.trim() && showTextbox) {
      // Save to localStorage immediately
      localStorage.setItem(`textbox_${itemId}`, currentContent);
      
      // Update the parent component with textResponse
      const updatedDocuments = [...documents];
      const textboxIndex = updatedDocuments.findIndex(doc => doc.type === 'textbox');
      
      if (textboxIndex >= 0) {
        updatedDocuments[textboxIndex] = {
          ...updatedDocuments[textboxIndex],
          content: currentContent
        };
      } else {
        updatedDocuments.push({
          id: `textbox_${itemId}`,
          name: 'Text Document',
          type: 'textbox',
          content: currentContent
        });
      }
      
      // Save documents (this updates parent state)
      saveDocuments(updatedDocuments);
      
      // Also update the textResponse field directly
      // Always call onTextResponseChange to ensure it's saved, even if content is empty
      if (onTextResponseChange) {
        onTextResponseChange({
          html: currentContent || '',
          plainText: (currentContent || '').replace(/<[^>]*>/g, '') // Strip HTML tags for plain text
        });
      }
      
      setHasTextbox(true);
      console.log(`Auto-saved textbox for ${itemId}, content length: ${currentContent.length}`);
    } else {
      // Even if content is empty, we should still save it to clear any existing content
      if (onTextResponseChange && showTextbox) {
        onTextResponseChange({
          html: '',
          plainText: ''
        });
      }
      console.log(`Auto-saved empty textbox for ${itemId}`);
    }
  };

  // Expose auto-save method to window for external access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any)[`autoSaveTextbox_${itemId}`] = autoSaveTextbox;
    }
  }, [textboxContent, showTextbox]);

  // Allow parent UI (e.g. header "Add" button) to open the same Add menu as the inline control
  useEffect(() => {
    if (typeof window === "undefined") return;
    (window as any)[`openAddMenu_${itemId}`] = () => setDropdownOpen(true);
    return () => {
      delete (window as any)[`openAddMenu_${itemId}`];
    };
  }, [itemId]);

  const handleCancelTextbox = () => {
    setShowTextbox(false);
    setTextboxContent('');
  };

  const handleClearTextbox = () => {
    localStorage.removeItem(`textbox_${itemId}`);
    setTextboxContent('');
    setHasTextbox(false);
    setShowTextbox(false);
  };

  const handleUpdateTextbox = (id: string, content: string) => {
    const updated = documents.map(doc => 
      doc.id === id ? { ...doc, content } : doc
    );
    saveDocuments(updated);
  };

  const handleRemoveDocument = (id: string) => {
    const updated = documents.filter(doc => doc.id !== id);
    saveDocuments(updated);
  };

  // Handle unlinking a file (removing section from db.json)
  const handleUnlinkFile = async (fileName: string) => {
    try {
      // Remove only the specific section (with question_id), keep the general section
      const sectionToRemove = await getSectionName(sectionKey, itemId);

      // Optimistically remove from local state immediately (non-blocking)
      startTransition(() => {
        setLinkedFiles(prev => prev.filter(file => file.name !== fileName));
        
        // Also remove from documents state if it exists there (to keep state in sync)
        setDocuments(prev => {
          const updated = prev.filter(doc => doc.name !== fileName);
          // Update parent component's documents state
          if (onDocumentsChange && updated.length !== prev.length) {
            // Defer parent callback to prevent UI freeze
            setTimeout(() => {
              onDocumentsChange(updated);
            }, 50);
          }
          return updated;
        });
      });

      // Remove section from db.json asynchronously
      if (window.sharePointAPI?.removeFileSection) {
        // Update db.json in background with delay to prevent blocking
        setTimeout(() => {
          window.sharePointAPI.removeFileSection({
            fileName,
            sectionToRemove
          }).then((result) => {
            if (result?.success) {
              console.log(`Successfully removed section '${sectionToRemove}' from file '${fileName}'`);
              // Reload linked files to reflect changes
              setTimeout(() => {
                loadLinkedFiles().catch(err => {
                  console.error('Failed to reload linked files:', err);
                });
              }, 500);
            } else {
              console.error('Failed to remove section from db.json:', result?.error);
              // Re-add file to UI if removal failed
              setTimeout(() => {
                loadLinkedFiles().catch(err => {
                  console.error('Failed to reload linked files:', err);
                });
              }, 500);
            }
          }).catch((error) => {
            console.error('Error removing section from db.json:', error);
            // Re-add file to UI if removal failed
            setTimeout(() => {
              loadLinkedFiles().catch(err => {
                console.error('Failed to reload linked files:', err);
              });
            }, 500);
          });
        }, 100); // Small delay to ensure UI is responsive
      }
    } catch (error) {
      console.error('Failed to unlink file:', error);
      // Re-add file to UI if removal failed
      setTimeout(() => {
        loadLinkedFiles().catch(err => {
          console.error('Failed to reload linked files:', err);
        });
      }, 500);
    }
  };

  // Files now open directly via href links like PPE execution phase


  // Combine linkedFiles and uploaded documents (excluding textbox)
  const allFiles = useMemo(() => {
    const uploadedFiles = documents.filter((doc: DocumentItem) => doc.type === 'uploaded');
    const linkedFileNames = new Set(linkedFiles.map((f: DocumentItem) => f.name));
    // Only show uploaded files that aren't already in linkedFiles
    const uniqueUploaded = uploadedFiles.filter((doc: DocumentItem) => !linkedFileNames.has(doc.name));
    return [...linkedFiles, ...uniqueUploaded];
  }, [linkedFiles, documents]);

  return (
    <div className="space-y-3" data-document-manager data-item-id={itemId}>
      {/* Display all files (linked + uploaded) above textbox */}
      {allFiles.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-white/80">Documents:</div>
          {allFiles.map((file) => (
            <div key={file.id} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/5 p-2">
              <FileText className="h-4 w-4 text-white/70 flex-shrink-0" />
              {file.url ? (
                <a 
                  href={file.url} 
                  target="_blank" 
                  rel="noreferrer" 
                  onClick={async (e) => {
                    e.preventDefault();
                    // Use Electron's shell.openExternal to open in default browser
                    if ((window as any).electronWindow?.openExternal) {
                      try {
                        await (window as any).electronWindow.openExternal(file.url!);
                      } catch (error) {
                        console.error("Error opening external URL:", error);
                        // Fallback to window.open if Electron API fails
                        window.open(file.url, "_blank", "noopener,noreferrer");
                      }
                    } else {
                      // Fallback to window.open if Electron API is not available
                      window.open(file.url, "_blank", "noopener,noreferrer");
                    }
                  }}
                  className="text-sm text-blue-300 hover:underline truncate flex-1 cursor-pointer"
                >
                  {file.name}
                </a>
              ) : (
                <div className="text-sm text-white truncate flex-1">
                  {file.name}
                </div>
              )}
              {!isArchivalMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (file.type === 'uploaded') {
                      handleRemoveDocument(file.id);
                    } else {
                      handleUnlinkFile(file.name);
                    }
                  }}
                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0 rounded-full"
                  disabled={effectiveDisabled}
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Document Dropdown */}
      <div className="flex items-center gap-2">
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-white/20 bg-black/40 text-white hover:bg-white/10"
              disabled={effectiveDisabled}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="border-white/20 bg-black/90 text-white">
            <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => handleAddTextbox(), 100); }} className="hover:bg-white/10">
              <FileText className="h-4 w-4 mr-2" />
              {hasTextbox ? 'Edit Textbox' : 'Textbox'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => setShowLinkDialog(true), 100); }} className="hover:bg-white/10">
              <Link className="h-4 w-4 mr-2" />
              Link existing workpaper
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => handleUploadFile(), 100); }} className="hover:bg-white/10">
              <Upload className="h-4 w-4 mr-2" />
              Upload file
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Inline Textbox Editor */}
      {showTextbox && (
        <div className="mt-3 p-4 border border-white/10 rounded bg-black/20">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-white">
                {hasTextbox ? 'Edit Text Document' : 'Text Document'}
              </h4>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelTextbox}
                  className="border-white/20 bg-transparent text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  Cancel
                </Button>
                {hasTextbox && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearTextbox}
                    className="border-red-500/20 bg-transparent text-red-400 hover:bg-red-500/10"
                    disabled={effectiveDisabled}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            
            {/* Inline Rich Text Editor */}
            <div className="space-y-2">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 p-2 border border-white/10 rounded bg-black/40">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('bold')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Bold className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('italic')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Italic className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('underline')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Underline className="h-4 w-4" />
                </Button>
                
                <div className="w-px h-6 bg-white/20 mx-1" />
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('insertUnorderedList')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('insertOrderedList')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <ListOrdered className="h-4 w-4" />
                </Button>
              </div>

              {/* Editor */}
              <div
                ref={textboxEditorRef}
                contentEditable={!disabled}
                onInput={(e) => {
                  const html = e.currentTarget.innerHTML;
                  setTextboxContent(html);
                  // Immediately save to localStorage
                  localStorage.setItem(`textbox_${itemId}`, html);
                  // Also immediately call onTextResponseChange to update parent state
                  if (onTextResponseChange) {
                    onTextResponseChange({
                      html: html || '',
                      plainText: (html || '').replace(/<[^>]*>/g, '')
                    });
                  }
                }}
                className="min-h-[200px] p-3 border border-white/10 rounded bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 prose prose-invert max-w-none"
                style={{ 
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}
                data-placeholder="Enter your text here..."
              />
            </div>
          </div>
        </div>
      )}

      {/* Link Document Dialog */}
      <LinkDocumentDialog
        isOpen={showLinkDialog}
        onClose={() => {
          setShowLinkDialog(false);
          // Reset linking state when dialog closes
          setIsLinking(false);
        }}
        onLink={handleLinkDocument}
      />
      
      {/* Background linking indicator (non-blocking) */}
      {isLinking && (
        <div className="fixed bottom-4 right-4 bg-black/80 text-white px-4 py-2 rounded-md shadow-lg flex items-center gap-2 z-50 border border-white/20">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Updating SharePoint...</span>
        </div>
      )}
    </div>
  );
}
